import React from 'react'
import Footer from './separate/Footer'
import { Box, Container, Typography, Button, Grid, Card, CardContent, Chip, Stack, alpha, Link as LinkMui } from '@mui/material'
import { FaChartLine, FaSignal, FaCrosshairs, FaBolt, FaShieldAlt, FaUsers, FaChartBar, FaTrophy, FaCheckCircle, FaRocket, FaCoins, FaClock, FaArrowUp } from 'react-icons/fa'
import { MdGeneratingTokens } from "react-icons/md";
import { Link, useNavigate } from 'react-router-dom'
import NavbarOne from './separate/NavbarOne'

const stats = [
  { value: '92%', label: 'Win Rate', icon: FaTrophy },
  { value: '300+', label: 'Signals Delivered', icon: FaSignal },
  { value: '400+', label: 'Futures Pairs', icon: MdGeneratingTokens },
  { value: '24/7', label: 'Market Coverage', icon: FaClock },
]

const signalTypes = [
  {
    title: 'Spot Trading Signals',
    description:
      'Low-risk signals for spot trading with clear entry, stop-loss, and 3 target levels for maximum profit optimization.',
    icon: FaCoins,
    features: [
      'Safe Entry Zones',
      '3 Take-Profit Targets',
      'Stop-Loss Protection',
      'Risk/Reward Analysis',
    ],
    gradient:
      'linear-gradient(135deg, rgba(29, 176, 161, 0.2) 0%, rgba(29, 176, 161, 0.05) 100%)',
    badge: 'Popular',
  },
  {
    title: 'Futures Trading Signals',
    description:
      'High-leverage futures signals with precise entries for maximizing gains on perpetual contracts.',
    icon: FaChartLine,
    features: [
      'Up to 125x Leverage',
      'Long & Short Positions',
      'Liquidation Alerts',
      'Margin Management',
    ],
    gradient:
      'linear-gradient(135deg, rgba(33, 202, 80, 0.2) 0%, rgba(33, 202, 80, 0.05) 100%)',
    badge: 'High Returns',
  },
]

const perfectTrade = {
  pair: 'BTC/USDT',
  type: 'LONG',
  leverage: '20x',
  entry: '$67,450',
  targets: [
    { level: 'Target 1', price: '$68,200', profit: '+1.1%', hit: true },
    { level: 'Target 2', price: '$69,500', profit: '+3.0%', hit: true },
    { level: 'Target 3', price: '$71,000', profit: '+5.3%', hit: true },
  ],
  stopLoss: '$66,200',
  result: '+106% ROI',
}

const features = [
  {
    icon: FaCrosshairs,
    title: '3 Target Strategy',
    description:
      'Every signal comes with 3 precise take-profit targets to lock in profits at different levels.',
  },
  {
    icon: FaShieldAlt,
    title: 'Risk Management',
    description:
      'Clear stop-loss levels and risk/reward ratios to protect your capital on every trade.',
  },
  {
    icon: FaBolt,
    title: 'Real-time Alerts',
    description:
      'Instant notifications via Telegram and app when signals are triggered or targets hit.',
  },
  {
    icon: FaChartBar,
    title: 'Performance Tracking',
    description:
      'Transparent track record with detailed analytics on all past signals and win rates.',
  },
]

const expertBenefits = [
  {
    icon: FaTrophy,
    title: 'Expert Analysis',
    description:
      'Trades are based on expert trading ratios and market analysis from seasoned professionals.',
  },
  {
    icon: FaUsers,
    title: 'Community Driven',
    description:
      'Join thousands of traders following expert signals with proven track records.',
  },
  {
    icon: FaSignal,
    title: 'Free Access',
    description:
      'All signals are completely free - no hidden charges, no subscriptions required.',
  },
]

const GlassCard = ({ children, sx = {}, className = '', ...props }) => (
  <Card
    sx={{
      bgcolor: '#14181f',
      backdropFilter: 'blur(10px)',
      // border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 4,
      color: 'white',
      transition: 'all 0.3s ease',
      '&:hover': {
        border: '1px solid rgba(29, 176, 161, 0.5)',
        bgcolor: 'rgba(255, 255, 255, 0.05)',
      },
      ...sx,
    }}
    className={className}
    {...props}
  >
    {children}
  </Card>
)

const Signals = (props) => {
  const primaryColor = '#1DAFA1' // Teal
  const successColor = '#21CA50' // Green
  const navigate = useNavigate()
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0E1116',
        color: 'white',
        overflowX: 'hidden',
      }}
    >
      <NavbarOne
        setTheme={props.setTheme}
        theme={props.theme}
      />
      {/* Hero Section */}
      <Box
        sx={{ position: 'relative', pt: 15, pb: 10, px: 2, overflow: 'hidden' }}
      >
        {/* Background Effects */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to bottom, ${alpha(primaryColor, 0.1)}, #0E1116)`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            left: '25%',
            width: 600,
            height: 600,
            bgcolor: alpha(primaryColor, 0.1),
            borderRadius: '50%',
            filter: 'blur(100px)',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '25%',
            right: '25%',
            width: 400,
            height: 400,
            bgcolor: alpha(successColor, 0.1),
            borderRadius: '50%',
            filter: 'blur(100px)',
            zIndex: 0,
          }}
        />

        <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Chip
              icon={<FaSignal color={primaryColor} size={16} />}
              label='Expert Trading Signals'
              sx={{
                mb: 3,
                bgcolor: alpha(primaryColor, 0.1),
                color: primaryColor,
                border: `1px solid ${alpha(primaryColor, 0.2)}`,
              }}
            />

            <Typography
              variant='h2'
              component='h1'
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.4rem', md: '3.2rem', lg: '4rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.5px',
                color: 'white',
              }}
            >
              Trade Smarter with{' '}
              <span style={{ color: primaryColor }}>92% Win Rate</span> on TradeIQ
            </Typography>

            <Typography
              variant='h6'
              sx={{
                color: '#9AA6B2',
                mb: 4,
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              Free expert-analyzed trading signals for Spot and Futures markets.
              Trades performed based on expert trading ratios with proven track
              records.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent='center'
            >
              <Button
                component="a"
                href='/blog/best-crypto-trading-signals-2026-guide'
                target="_blank"
                rel="noopener noreferrer"
                variant='contained'
                size='medium'
                endIcon={<FaArrowUp style={{ transform: 'rotate(45deg)' }} />}
                sx={{
                  bgcolor: primaryColor,
                  color: 'white',
                  fontSize: '1rem',
                  py: 1,
                  px: 3,
                  borderRadius: 2,
                  '&:hover': { bgcolor: alpha(primaryColor, 0.9) },
                }}
              >
                CRYPTO TRADING SIGNALS
              </Button>
              <Button
                variant='outlined'
                size='medium'
                onClick={() => navigate('/my/dashboard', { state: { tab: 8 } })}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.2)',
                  fontSize: '1rem',
                  py: 1,
                  px: 3,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                View Performance
              </Button>
            </Stack>
          </Box>

          {/* Stats */}
          <Grid container spacing={3} sx={{ mt: 8 }}>
            {stats.map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <GlassCard sx={{ textAlign: 'center', p: 2 }}>
                  <Box
                    sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}
                  >
                    <stat.icon size={32} color={primaryColor} />
                  </Box>
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                      color: primaryColor,
                      mb: 0.5,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {stat.label}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Signal Types */}
      <Box sx={{ py: 10, px: 2 }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant='h3'
              sx={{
                fontWeight: 600,
                mb: 2,
                color: 'white',
                fontSize: { xs: '1.5rem', md: '2rem', lg: '2.2rem' },
              }}
            >
              Signals for Every{' '}
              <span style={{ color: primaryColor }}>Trading Style</span>
            </Typography>
            <Typography
              variant='h6'
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              Whether you prefer safe spot trading or high-leverage futures,
              we've got you covered.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {signalTypes.map((type) => (
              <Grid item xs={12} md={6} key={type.title}>
                <GlassCard
                  className='signal-card'
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: type.gradient,
                      opacity: 0,
                      transition: 'opacity 0.5s',
                      '.signal-card:hover &': { opacity: 1 },
                    }}
                  />
                  <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 4,
                          bgcolor: alpha(primaryColor, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <type.icon size={32} color={primaryColor} />
                      </Box>
                      <Chip
                        label={type.badge}
                        sx={{
                          bgcolor: alpha(primaryColor, 0.1),
                          color: primaryColor,
                        }}
                      />
                    </Box>

                    <Typography
                      variant='h5'
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'white',
                        fontSize: { xs: '1.1rem', md: '1.5rem' },
                      }}
                    >
                      {type.title}
                    </Typography>
                    <Typography
                      variant='body1'
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        mb: 4,
                        fontSize: { xs: '0.85rem', md: '1rem' },
                      }}
                    >
                      {type.description}
                    </Typography>
                    <br />
                    <Stack spacing={1}>
                      {type.features.map((feature) => (
                        <Box
                          key={feature}
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <FaCheckCircle color={primaryColor} size={20} />
                          <Typography sx={{ color: 'white' }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Perfect Trade Example */}
      <Box sx={{ py: 10, px: 2, position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to right, ${alpha(primaryColor, 0.05)}, transparent, ${alpha(successColor, 0.05)})`,
          }}
        />

        <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              icon={<FaTrophy size={16} />}
              label='Real Trade Example'
              sx={{
                mb: 2,
                bgcolor: alpha(successColor, 0.1),
                color: successColor,
                border: `1px solid ${alpha(successColor, 0.2)}`,
                '& .MuiChip-icon': { color: successColor },
              }}
            />
            <Typography
              variant='h3'
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'white',
                fontSize: { xs: '1.5rem', md: '2rem', lg: '2.2rem' },
              }}
            >
              Anatomy of a{' '}
              <span style={{ color: primaryColor }}>Perfect Trade</span>
            </Typography>
            <Typography
              variant='h6'
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              See how our 3-target strategy maximizes profits on every winning
              trade.
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {/* <GlassCard> */}
            <Card
              sx={{
                bgcolor: '#14181f',
                backdropFilter: 'blur(10px)',
                borderRadius: 4,
                color: 'white',
                transition: 'all 0.3s ease',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Trade Header */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mb: 4,
                    pb: 3,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        bgcolor: alpha(primaryColor, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FaChartLine size={28} color={primaryColor} />
                    </Box>
                    <Box>
                      <Typography
                        variant='h5'
                        sx={{
                          fontWeight: 700,
                          color: 'white',
                          fontSize: { xs: '1.2rem', md: '1.5rem' },
                        }}
                      >
                        {perfectTrade.pair}
                      </Typography>
                      <Stack direction='row' spacing={1} sx={{ mt: 0.5 }}>
                        <Chip
                          label={perfectTrade.type}
                          size='small'
                          sx={{
                            bgcolor: alpha(successColor, 0.1),
                            color: successColor,
                            borderRadius: 1,
                            fontSize: { xs: '0.7rem', md: '0.8125rem' },
                            height: { xs: 20, md: 24 },
                          }}
                        />
                        <Chip
                          label={perfectTrade.leverage}
                          variant='outlined'
                          size='small'
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.2)',
                            borderRadius: 1,
                            fontSize: { xs: '0.7rem', md: '0.8125rem' },
                            height: { xs: 20, md: 24 },
                          }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                      }}
                    >
                      Final Result
                    </Typography>
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 700,
                        color: successColor,
                        fontSize: { xs: '1.5rem', md: '2.125rem' },
                      }}
                    >
                      {perfectTrade.result}
                    </Typography>
                  </Box>
                </Box>

                {/* Entry & Stop Loss */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderRadius: 3,
                        p: 2,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}
                      >
                        Entry Price
                      </Typography>
                      <Typography
                        variant='h5'
                        sx={{ fontWeight: 700, color: primaryColor }}
                      >
                        {perfectTrade.entry}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderRadius: 3,
                        p: 2,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}
                      >
                        Stop Loss
                      </Typography>
                      <Typography
                        variant='h5'
                        sx={{ fontWeight: 700, color: '#ff4444' }}
                      >
                        {perfectTrade.stopLoss}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Targets */}
                <Stack spacing={2}>
                  <Typography
                    variant='subtitle2'
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    Take Profit Targets
                  </Typography>
                  {perfectTrade.targets.map((target) => (
                    <Box
                      key={target.level}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: alpha(successColor, 0.05),
                        border: `1px solid ${alpha(successColor, 0.2)}`,
                        borderRadius: 3,
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: alpha(successColor, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FaCrosshairs size={20} color={successColor} />
                        </Box>
                        <Box>
                          <Typography
                            variant='body1'
                            sx={{ fontWeight: 500, color: 'white' }}
                          >
                            {target.level}
                          </Typography>
                          <Typography
                            variant='h6'
                            sx={{ fontWeight: 700, color: 'white' }}
                          >
                            {target.price}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant='h6'
                          sx={{ fontWeight: 700, color: successColor }}
                        >
                          {target.profit}
                        </Typography>
                        <Chip
                          icon={<FaCheckCircle size={12} />}
                          label='Hit'
                          size='small'
                          sx={{
                            mt: 0.5,
                            bgcolor: alpha(successColor, 0.1),
                            color: successColor,
                            '& .MuiChip-icon': { color: successColor },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
            {/* </GlassCard> */}
          </Box>
        </Container>
      </Box>

      {/* Features Grid */}
      <Box sx={{ py: 10, px: 2 }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant='h4'
              sx={{ fontWeight: 700, mb: 2, color: 'white' }}
            >
              Why Traders{' '}
              <span style={{ color: primaryColor }}>Trust Our TradeIQ</span>
            </Typography>
            <Typography
              variant='h6'
              sx={{
                color: '#9AA6B2',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Our signals are based on expert trading ratios - completely free,
              no strings attached.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature) => (
              <Grid item xs={12} sm={6} lg={3} key={feature.title}>
                <GlassCard
                  sx={{
                    height: '100%',
                    p: 3,
                    '&:hover': { transform: 'translateY(-5px)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      bgcolor: alpha(primaryColor, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <feature.icon size={28} color={primaryColor} />
                  </Box>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, mb: 1, color: 'white' }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant='body2' sx={{ color: '#9AA6B2' }}>
                    {feature.description}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* High Leverage Futures */}
      <Box sx={{ py: 10, px: 2, position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to bottom, #0E1116, rgba(255,255,255,0.02), #0E1116)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: 0,
            width: 500,
            height: 500,
            bgcolor: alpha(primaryColor, 0.05),
            borderRadius: '50%',
            filter: 'blur(100px)',
            transform: 'translate(50%, -50%)',
          }}
        />

        <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems='center'>
            <Grid item xs={12} lg={6}>
              <Chip
                icon={<FaRocket size={16} />}
                label='High Leverage Trading'
                sx={{
                  mb: 3,
                  bgcolor: alpha('#ff9800', 0.1),
                  color: '#ff9800',
                  border: `1px solid ${alpha('#ff9800', 0.2)}`,
                  '& .MuiChip-icon': { color: '#ff9800' },
                }}
              />

              <Typography
                variant='h4'
                sx={{ fontWeight: 600, mb: 3, color: 'white' }}
              >
                Maximize Gains with{' '}
                <span style={{ color: primaryColor }}>High Leverage</span>{' '}
                Futures Signals
              </Typography>

              <Typography
                variant='h6'
                sx={{
                  color: '#9AA6B2',
                  mb: 4,
                  fontWeight: 400,
                }}
              >
                Our expert analysts identify high-probability setups perfect for
                leveraged trading. Get precise entries optimized for 10x to 125x
                leverage positions.
              </Typography>

              <Stack spacing={2} sx={{ mb: 4 }}>
                {[
                  'Optimized for 10x - 125x leverage',
                  'Long and Short position signals',
                  'Liquidation price warnings',
                  'Real-time position management alerts',
                  'Risk-adjusted position sizing',
                ].map((item) => (
                  <Box
                    key={item}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: alpha(primaryColor, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FaCheckCircle size={14} color={primaryColor} />
                    </Box>
                    <Typography sx={{ color: 'white' }}>{item}</Typography>
                  </Box>
                ))}
              </Stack>

              <Button
                variant='contained'
                onClick={() => navigate('/my/dashboard', { state: { tab: 8 } })}
                size='small'
                endIcon={<FaArrowUp style={{ transform: 'rotate(45deg)' }} />}
                sx={{
                  bgcolor: primaryColor,
                  color: 'white',
                  fontSize: '1rem',
                  py: 1,
                  px: 4,
                  borderRadius: 2,
                  '&:hover': { bgcolor: alpha(primaryColor, 0.9) },
                }}
              >
                Get Free Futures Signals
              </Button>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Grid container spacing={2}>
                {[
                  { leverage: '10x', risk: 'Low', color: successColor },
                  { leverage: '25x', risk: 'Medium', color: primaryColor },
                  { leverage: '50x', risk: 'High', color: '#ff9800' },
                  { leverage: '125x', risk: 'Expert', color: '#ff4444' },
                ].map((item) => (
                  <Grid item xs={6} key={item.leverage}>
                    <GlassCard sx={{ textAlign: 'center', p: 3 }}>
                      <Typography
                        variant='h4'
                        sx={{ fontWeight: 600, color: item.color, mb: 1 }}
                      >
                        {item.leverage}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        Leverage
                      </Typography>
                      <Chip
                        label={`${item.risk} Risk`}
                        variant='outlined'
                        size='small'
                        sx={{
                          mt: 2,
                          color: 'white',
                          borderColor: 'rgba(255,255,255,0.2)',
                        }}
                      />
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Expert Benefits */}
      <Box sx={{ py: 10, px: 2 }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              icon={<FaUsers size={16} />}
              label='100% Free'
              sx={{
                mb: 2,
                bgcolor: alpha(primaryColor, 0.1),
                color: primaryColor,
                border: `1px solid ${alpha(primaryColor, 0.2)}`,
                '& .MuiChip-icon': { color: primaryColor },
              }}
            />
            <Typography
              variant='h4'
              sx={{ fontWeight: 600, mb: 2, color: 'white' }}
            >
              Why Follow{' '}
              <span style={{ color: primaryColor }}>Expert Signals</span>
            </Typography>
            <Typography
              variant='h6'
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Our signals are based on expert trading ratios - completely free,
              no strings attached.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {expertBenefits.map((benefit) => (
              <Grid item xs={12} md={4} key={benefit.title}>
                <GlassCard
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    height: '100%',
                    '&:hover': {
                      borderColor: alpha(primaryColor, 0.5),
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      bgcolor: alpha(primaryColor, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      mx: 'auto',
                    }}
                  >
                    <benefit.icon size={32} color={primaryColor} />
                  </Box>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, mb: 1, color: 'white' }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography variant='body1' sx={{ color: '#9AA6B2' }}>
                    {benefit.description}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ pt: { xs: 8, md: 10 }, px: 2, mb: 4 }}>
        <Container maxWidth='lg'>
          <GlassCard
            sx={{
              p: { xs: 3, md: 4 },
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(to right, ${alpha(primaryColor, 0.1)}, transparent, ${alpha(primaryColor, 0.1)})`,
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ mb: 1.5, display: 'inline-block' }}>
                <FaSignal size={48} color={primaryColor} />
              </Box>
              <Typography
                variant='h4'
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  color: 'white',
                  fontSize: { xs: '1.5rem', md: '2.125rem' },
                }}
              >
                Start Trading with Expert TradeIQ
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  color: '#9AA6B2',
                  mb: 2.5,
                  // maxWidth: 600,
                  mx: 'auto',
                  fontWeight: 400,
                  fontSize: { xs: '0.9rem', md: '1.25rem' },
                }}
              >
                Join thousands of traders following expert trading ratios -
                completely free.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent='center'
              >
                <Button
                  variant='contained'
                  size='medium'
                  onClick={() => navigate('/my/dashboard', { state: { tab: 8 } })}
                  endIcon={<FaArrowUp style={{ transform: 'rotate(45deg)' }} />}
                  sx={{
                    bgcolor: primaryColor,
                    color: 'white',
                    fontSize: '1rem',
                    py: 1,
                    px: 3,
                    borderRadius: 2,
                    '&:hover': { bgcolor: alpha(primaryColor, 0.9) },
                  }}
                >
                  Get Free Signals Now
                </Button>
                <Button
                  component={Link}
                  to='/'
                  variant='outlined'
                  size='medium'
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.2)',
                    fontSize: '1rem',
                    py: 1,
                    px: 3,
                    borderRadius: 2,
                    textDecoration: 'none',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  Back to Home
                </Button>
              </Stack>
            </Box>
          </GlassCard>
        </Container>
      </Box>
      {/* onClick={() => navigate('/blog/telegram-channel-for-crypto-trading-signals')} */}
      <Box sx={{ pb: { xs: 4, md: 6 }, px: 2, mb: 10, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {/* Know More Button */}
            <Button
              variant="outlined"
              sx={{
                color: '#fff',
                borderColor: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                // borderRadius: '20px',
                px: 2,
                '&:hover': {
                  borderColor: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Know More :
            </Button>

            {/* Link Text */}
            <LinkMui
              underline="always"
              href='/blog/telegram-channel-for-crypto-trading-signals'
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'underline !important',
                color: '#9AA6B2',
                fontSize: { xs: '0.9rem', md: '1.1rem' },
                //  fontSize: { xs: '0.9rem', md: '1.25rem' },
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Best Telegram Channel for Crypto Trading Signals <FaArrowUp style={{ transform: 'rotate(45deg)' }} />
            </LinkMui>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  )
}

export default Signals
